import { FC } from "react";

import { Stack } from "@mui/material";
import { DonorRoutes } from "@simplyk/common";

import { AssistantHelpGuide, AssistantOrganization } from "./helpers/parseOrganizationDashboards";

import { useTranslate } from "@/common/hooks/useTranslate";
import { DashboardButton } from "@/dashboard-design-system/DashboardButton";
import { DashboardTypography } from "@/dashboard-design-system/Typography";
import { Add, ArrowRight, ExternalLink } from "@/icons/outlined";

const HELP_CENTER_URL = "https://support.zeffy.com";

interface OrganizationDashboardsProps {
  organizations: AssistantOrganization[];
  guide?: AssistantHelpGuide | null;
  onOpen: (organization: AssistantOrganization) => Promise<void>;
  onCreate: () => void;
}

const HelpGuide: FC<{ guide: AssistantHelpGuide | null }> = ({ guide }) => {
  const { t } = useTranslate();
  return (
    <DashboardButton
      href={guide?.url ?? HELP_CENTER_URL}
      target="_blank"
      variant="tonal"
      size="medium"
      endIcon={<ExternalLink />}
      fullWidth
      aria-label={
        guide ? t("dashboard", "donorAssistant.organizationDashboards.guide", { title: guide.title }) : undefined
      }
      data-test="donor-assistant-organization-guide"
    >
      {guide?.title ?? t("dashboard", "donorAssistant.organizationDashboards.helpCenter")}
    </DashboardButton>
  );
};

export const OrganizationDashboards: FC<OrganizationDashboardsProps> = ({ organizations, guide, onOpen, onCreate }) => {
  const { t } = useTranslate();
  const helpGuide = guide !== undefined && <HelpGuide guide={guide} />;
  if (organizations.length === 0) {
    return (
      <Stack spacing={1} data-test="donor-assistant-organization-dashboards">
        {helpGuide}
        <DashboardButton
          href={DonorRoutes.CreateNewOrganization}
          onClick={onCreate}
          variant="outlined"
          size="medium"
          startIcon={<Add />}
          fullWidth
        >
          {t("dashboard", "donorAssistant.organizationDashboards.create")}
        </DashboardButton>
        <DashboardTypography variant="body2" vibe="text-neutral-quiet">
          {t("dashboard", "donorAssistant.organizationDashboards.otherEmail")}
        </DashboardTypography>
      </Stack>
    );
  }
  return (
    <Stack spacing={1} data-test="donor-assistant-organization-dashboards">
      {helpGuide}
      {organizations.map((organization) => (
        <DashboardButton
          key={organization.id}
          onClick={() => onOpen(organization)}
          variant="outlined"
          size="medium"
          endIcon={<ArrowRight />}
          fullWidth
          aria-label={t("dashboard", "donorAssistant.organizationDashboards.open", {
            organization: organization.name,
          })}
        >
          {organization.name}
        </DashboardButton>
      ))}
    </Stack>
  );
};
